import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { PlanProvider } from './contexts/PlanContext';
import { Toaster } from './components/ui/sonner';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardLayout from './components/DashboardLayout';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import AuthCallback from './pages/AuthCallback';
import DashboardPage from './pages/DashboardPage';
import ProjectsPage from './pages/ProjectsPage';
import InventoryPage from './pages/InventoryPage';
import ChannelsPage from './pages/ChannelsPage';
import UsersPage from './pages/UsersPage';
import AuditPage from './pages/AuditPage';
import PricingPage from './pages/PricingPage';
import SettingsPage from './pages/SettingsPage';
import ContactsPage from './pages/ContactsPage';
import ChannelPartnerPortal from './pages/ChannelPartnerPortal';
import RequestPricingPage from './pages/RequestPricingPage';

// Platform Admin (completely separate)
import PlatformLoginPage from './pages/platform/PlatformLoginPage';
import PlatformLayout from './pages/platform/PlatformLayout';
import PlatformDashboard from './pages/platform/PlatformDashboard';
import PlatformCompanies from './pages/platform/PlatformCompanies';
import PlatformSubscriptions from './pages/platform/PlatformSubscriptions';
import PlatformDiscounts from './pages/platform/PlatformDiscounts';
import PlatformOnboarding from './pages/platform/PlatformOnboarding';
import PlatformAudit from './pages/platform/PlatformAudit';
import PlatformPricingRequests from './pages/platform/PlatformPricingRequests';

import './App.css';

// Router wrapper to handle session_id in URL
const AppRouter = () => {
  const location = useLocation();
  
  // Check URL fragment for session_id (from Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/request-pricing" element={<RequestPricingPage />} />
      
      {/* Protected routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardLayout>
            <DashboardPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/projects" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ProjectsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/inventory" element={
        <ProtectedRoute>
          <DashboardLayout>
            <InventoryPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/channels" element={
        <ProtectedRoute allowedRoles={['admin', 'management']}>
          <DashboardLayout>
            <ChannelsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/users" element={
        <ProtectedRoute allowedRoles={['admin', 'management']}>
          <DashboardLayout>
            <UsersPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/audit" element={
        <ProtectedRoute allowedRoles={['admin', 'management']}>
          <DashboardLayout>
            <AuditPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/pricing" element={<Navigate to="/request-pricing" replace />} />
      
      <Route path="/contacts" element={
        <ProtectedRoute allowedRoles={['admin', 'management', 'sales']}>
          <DashboardLayout>
            <ContactsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <DashboardLayout>
            <SettingsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      {/* Channel Partner Portal - Simplified UI */}
      <Route path="/partner" element={
        <ProtectedRoute allowedRoles={['channel_partner']}>
          <ChannelPartnerPortal />
        </ProtectedRoute>
      } />
      
      {/* Platform Admin - Completely Separate */}
      <Route path="/platform-admin/login" element={<PlatformLoginPage />} />
      <Route path="/platform-admin" element={<PlatformLayout />}>
        <Route index element={<Navigate to="/platform-admin/dashboard" replace />} />
        <Route path="dashboard" element={<PlatformDashboard />} />
        <Route path="companies" element={<PlatformCompanies />} />
        <Route path="subscriptions" element={<PlatformSubscriptions />} />
        <Route path="discounts" element={<PlatformDiscounts />} />
        <Route path="pricing-requests" element={<PlatformPricingRequests />} />
        <Route path="onboarding" element={<PlatformOnboarding />} />
        <Route path="audit" element={<PlatformAudit />} />
      </Route>
      
      {/* Default catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <PlanProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </PlanProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
